import { useDispatch, useSelector } from 'utils/hooks'
import { Modal } from '@jisr-hr/design-system'
import { Typography, Spacer, Button, Flex } from '@jisr-hr/ds'
import i18n from 'translations/i18n'
import { FormApi } from 'final-form'
import {
  undoOnboardingAdmin,
  listOnboardingAdmins,
  inviteOnboardingAdmin,
  deleteOnboardingAdmin,
} from 'redux/organizationOnborading/inviteAdmins/actionCreators'
import { OnboardingAdminType } from 'redux/organizationOnborading/inviteAdmins/type'
import InviteForm from './components/InviteForm'
import FailuresList from './components/FailuresList'
import InvitedAdmins from './components/InvitedAdmins'

type OnboardingInviteAdminsProps = {
  isOpen: boolean
  onClose: () => void
}

const invitedStatus = ['accepted', 'expired', 'pending', 'initiated']

const OnboardingInviteAdmins = (props: OnboardingInviteAdminsProps): JSX.Element => {
  const { isOpen } = props
  const dispatch = useDispatch()

  const { employee } = useSelector(({ auth }) => auth)
  const { onboarding_admins, fetching } = useSelector(
    ({ onboardingInviteAdmins }) => onboardingInviteAdmins,
  )

  const invitedAdmins =
    onboarding_admins.filter(
      (e) => invitedStatus.includes(e.invitation_status || '') && e.id !== employee?.id,
    ) || []
  const failuresList =
    onboarding_admins.filter((e) =>
      ['failed', 'not_delivered'].includes(e.invitation_status || ''),
    ) || []

  const handleOnSubmit = (
    values: { email: string },
    fun: FormApi<{ email: string }, Partial<{ email: string }>>,
  ): void => {
    dispatch(inviteOnboardingAdmin(values.email)).then(() => {
      dispatch(listOnboardingAdmins())
      fun.reset()
    })
  }

  const handleOnClick = (values: OnboardingAdminType): void => {
    if (values.invitation_status === 'accepted') {
      dispatch(deleteOnboardingAdmin(values.id)).then(() => {
        dispatch(listOnboardingAdmins())
      })
    }
    if (['pending', 'initiated'].includes(values.invitation_status || '')) {
      dispatch(undoOnboardingAdmin(values.email)).then(() => {
        dispatch(listOnboardingAdmins())
      })
    }
    if (['failed', 'not_delivered', 'expired'].includes(values.invitation_status || '')) {
      dispatch(inviteOnboardingAdmin(values.email)).then(() => {
        dispatch(listOnboardingAdmins())
      })
    }
  }

  return (
    <Modal
      width={600}
      isOpen={isOpen}
      variant="centerDialog"
      onClose={props.onClose}
      content={
        <div style={{ padding: 24 }}>
          <InviteForm
            fetching={fetching}
            onSubmit={handleOnSubmit}
          />
          {!!failuresList.length && (
            <>
              <Spacer height={24} />
              <FailuresList
                fetching={fetching}
                list={failuresList}
                onClick={handleOnClick}
              />
            </>
          )}

          <Spacer height={24} />
          <Typography
            variant="title-1"
            style={{ color: 'var(--ds-base-color-content-on-neutral-fg)' }}
            text={i18n.t('label.invited_admins')}
          />
          {!!invitedAdmins.length && (
            <>
              <Spacer height={10} />
              <InvitedAdmins
                fetching={fetching}
                list={invitedAdmins}
                onClick={handleOnClick}
              />
            </>
          )}

          <Spacer height={24} />
          <Flex justifyEnd>
            <Button
              size="sm"
              variant="tertiaryGray"
              label={i18n.t('label.cancel')}
              onClick={props.onClose}
            />
          </Flex>
        </div>
      }
    />
  )
}

export default OnboardingInviteAdmins
